import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Route as RouteIcon, Plus, ArrowLeft, ArrowUp, ArrowDown, Trash2, Play, UserCheck, X, MapPin, Sparkles, Navigation } from "lucide-react";
import RouteMap from "../../components/RouteMap";

const PURPLE = "#2B1E4A";
const MAGENTA = "#B5179E";
const GOLD = "#F5B400";

const RST = {
  DRAFT: ["Rascunho", "bg-gray-100 text-gray-600"],
  ASSIGNED: ["Atribuída", "bg-amber-50 text-amber-700"],
  IN_PROGRESS: ["Em andamento", "bg-emerald-50 text-emerald-700"],
  COMPLETED: ["Concluída", "bg-blue-50 text-blue-700"],
  CANCELLED: ["Cancelada", "bg-gray-100 text-gray-400"],
};
const SST = {
  PENDING: ["Pendente", "#8A7788"], OUT_FOR_DELIVERY: ["Saiu", "#C9960A"],
  ARRIVED: ["Chegou", "#C9960A"], DELIVERED: ["Entregue", "#2563EB"],
  FAILED: ["Falhou", "#B45309"], SKIPPED: ["Pulada", "#6B5A69"],
};

const addr = (a) => (a ? [a.street, a.number, a.city].filter(Boolean).join(", ") : "—");
const bu = (u) => (u === "ELETROS" ? "Eletros" : "Cris Lingerie");
const fmtKm = (m) => (m == null ? "—" : `${(m / 1000).toFixed(1).replace(".", ",")} km`);
const fmtDur = (s) => {
  if (s == null) return "—";
  const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60);
  return h > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${m} min`;
};
const fmtEta = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); }
  catch { return null; }
};

export default function LogisticaRoutes() {
  const [routes, setRoutes] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [sel, setSel] = useState(null); // route detail
  const [avail, setAvail] = useState([]);
  const [creating, setCreating] = useState(false);
  const [mapsEnabled, setMapsEnabled] = useState(false);
  const [mapsKey, setMapsKey] = useState("");
  const [optPreview, setOptPreview] = useState(null);
  const [optimizing, setOptimizing] = useState(false);

  const loadRoutes = useCallback(() => {
    api.get("/routes").then(({ data }) => setRoutes(data)).catch(() => setRoutes([]));
  }, []);
  useEffect(() => {
    loadRoutes();
    api.get("/delivery-drivers").then(({ data }) => setDrivers(data)).catch(() => setDrivers([]));
    api.get("/public/delivery/maps-config").then(({ data }) => { setMapsEnabled(!!data.enabled); setMapsKey(data.browser_api_key || ""); }).catch(() => setMapsEnabled(false));
  }, [loadRoutes]);

  const openRoute = async (id) => {
    try {
      const { data } = await api.get(`/routes/${id}`);
      setSel(data);
      const { data: av } = await api.get("/deliveries-available");
      setAvail(av);
    } catch { toast.error("Erro ao abrir rota"); }
  };
  const refresh = async () => { if (sel) await openRoute(sel.id); loadRoutes(); };

  const createRoute = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/routes", { name: `Rota ${new Date().toLocaleDateString("pt-BR")}` });
      toast.success("Rota criada");
      loadRoutes();
      openRoute(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setCreating(false); }
  };

  const addStop = async (oid) => {
    try { await api.post(`/routes/${sel.id}/stops`, { delivery_order_id: oid }); toast.success("Entrega adicionada"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const removeStop = async (sid) => {
    try { await api.delete(`/routes/${sel.id}/stops/${sid}`); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const move = async (idx, dir) => {
    const ids = sel.stops.map((s) => s.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try { await api.put(`/routes/${sel.id}/reorder`, { stop_ids: ids }); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const assign = async (driver_id) => {
    if (!driver_id) return;
    try { await api.post(`/routes/${sel.id}/assign`, { driver_id }); toast.success("Entregador atribuído"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const start = async () => {
    try { await api.post(`/routes/${sel.id}/start`); toast.success("Rota iniciada"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancelar esta rota? As entregas voltam para 'Liberadas'.")) return;
    try { await api.post(`/routes/${sel.id}/cancel`); toast.success("Rota cancelada"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const geocodeBatch = async () => {
    try {
      const { data } = await api.post("/deliveries/geocode-batch", { limit: 50 });
      toast.success(`Geocodificação: ${data.ok} ok, ${data.failed} falhas`);
      refresh();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((d && d.message) || "Geocodificação indisponível");
    }
  };

  const runOptimize = async () => {
    setOptimizing(true);
    try {
      const { data } = await api.post(`/delivery-routes/${sel.id}/optimize`);
      setOptPreview(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && d.code === "SIGNATURE_BLOCKED") toast.error("Há entregas com promissória pendente. Regularize antes de otimizar.");
      else toast.error((d && d.message) || "Não foi possível otimizar agora. A ordem manual foi preservada.");
    } finally { setOptimizing(false); }
  };
  const applyOptimize = async () => {
    const s = optPreview.suggestion;
    try {
      await api.post(`/delivery-routes/${sel.id}/optimize/apply`, {
        ordered_stop_ids: s.ordered_stop_ids, total_distance_meters: s.total_distance_meters,
        total_duration_seconds: s.total_duration_seconds, polyline: s.polyline, stops: s.stops,
      });
      toast.success("Rota otimizada aplicada");
      setOptPreview(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao aplicar"); }
  };

  // ---- Detail view ----
  if (sel) {
    const mutable = sel.status === "DRAFT" || sel.status === "ASSIGNED";
    const [rlabel, rcls] = RST[sel.status] || ["—", "bg-gray-100"];
    return (
      <div className="space-y-4 p-4 lg:p-8" data-testid="rota-route-detail">
        <button onClick={() => setSel(null)} className="flex items-center gap-1 text-sm font-bold text-[#5B2CA0]" data-testid="rota-back-btn"><ArrowLeft size={16} /> Voltar</button>
        <div className="flex flex-wrap items-center gap-3 rounded-2xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${PURPLE}, ${MAGENTA})` }}>
          <span className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: GOLD }}><RouteIcon size={22} className="text-[#2B1E4A]" /></span>
          <div className="flex-1"><div className="text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: GOLD }}>CRIS ROTA</div><h1 className="font-display text-lg font-bold">{sel.name || "Rota"}</h1></div>
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${rcls}`} data-testid="rota-route-status">{rlabel}</span>
        </div>

        {/* Ações da rota */}
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-white p-4">
          <select defaultValue={sel.driver_id || ""} disabled={!mutable} onChange={(e) => assign(e.target.value)} className="rounded-xl border border-border px-3 py-2 text-sm disabled:opacity-50" data-testid="rota-assign-driver">
            <option value="">{sel.driver_name ? sel.driver_name : "Escolher entregador…"}</option>
            {drivers.filter((d) => d.active).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          {sel.status === "ASSIGNED" && <button onClick={start} className="flex items-center gap-1 rounded-xl px-3 py-2 text-sm font-bold text-white" style={{ background: "#059669" }} data-testid="rota-start-btn"><Play size={15} /> Iniciar rota</button>}
          {mutable && (mapsEnabled ? (
            <button onClick={runOptimize} disabled={optimizing || sel.stops.length < 2} className="flex items-center gap-1 rounded-xl px-3 py-2 text-sm font-bold text-white disabled:opacity-50" style={{ background: MAGENTA }} data-testid="rota-optimize-btn"><Sparkles size={15} /> {optimizing ? "Otimizando…" : "Otimizar rota"}</button>
          ) : (
            <span className="rounded-xl border border-border px-3 py-2 text-[12px] font-bold text-[#9A8A98]" data-testid="rota-optimize-disabled">Otimização indisponível</span>
          ))}
          {mutable && mapsEnabled && <button onClick={geocodeBatch} className="flex items-center gap-1 rounded-xl border border-border px-3 py-2 text-[12px] font-bold text-[#5B2CA0]" data-testid="rota-geocode-btn"><MapPin size={14} /> Geocodificar</button>}
          {mutable && <button onClick={cancel} className="flex items-center gap-1 rounded-xl border border-red-200 px-3 py-2 text-sm font-bold text-red-600" data-testid="rota-cancel-btn"><X size={15} /> Cancelar</button>}
          {sel.driver_name && <span className="flex items-center gap-1 text-[12px] font-bold text-[#6B5A69]"><UserCheck size={14} /> {sel.driver_name}</span>}
        </div>

        {/* Mapa + métricas */}
        <RouteMap apiKey={mapsKey} enabled={mapsEnabled} origin={sel.origin_lat != null ? { lat: sel.origin_lat, lng: sel.origin_lng } : null} stops={sel.stops} polyline={sel.optimization_polyline} />
        {(sel.optimization_provider === "GOOGLE" || sel.estimated_distance_meters != null) && (
          <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-border bg-white p-4" data-testid="rota-metrics">
            {sel.optimization_provider === "GOOGLE" && <span className="flex items-center gap-1 rounded-full bg-fuchsia-50 px-2 py-0.5 text-[11px] font-bold text-fuchsia-700"><Sparkles size={12} /> Rota otimizada pelo Google</span>}
            <div><div className="text-[10px] uppercase tracking-widest text-[#9A8A98]">Distância</div><div className="font-display text-lg font-bold" style={{ color: PURPLE }}>{fmtKm(sel.estimated_distance_meters)}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-[#9A8A98]">Duração estimada</div><div className="font-display text-lg font-bold" style={{ color: PURPLE }}>{fmtDur(sel.estimated_duration_seconds)}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-[#9A8A98]">Paradas</div><div className="font-display text-lg font-bold" style={{ color: PURPLE }}>{sel.stops.length}</div></div>
          </div>
        )}

        {/* Paradas */}
        <div>
          <h2 className="mb-2 text-sm font-bold text-brand-dark">Paradas ({sel.stops.length})</h2>
          {sel.stops.length === 0 ? <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-[#6B5A69]">Nenhuma parada. Adicione entregas liberadas abaixo.</div> : (
            <div className="space-y-2" data-testid="rota-stops-list">
              {sel.stops.map((s, idx) => {
                const [slabel, scolor] = SST[s.status] || ["—", "#8A7788"];
                return (
                  <div key={s.id} className="flex items-center gap-3 rounded-2xl border border-border bg-white p-3" data-testid={`rota-stop-${s.id}`}>
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-bold text-white" style={{ background: PURPLE }}>{s.position}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-bold text-brand-dark">{s.order?.customer_name || "Cliente"}</div>
                      <div className="flex items-start gap-1 text-[12px] text-[#8A7788]"><MapPin size={13} className="mt-0.5 shrink-0" /> {addr(s.order?.delivery_address_snapshot)}</div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wide" style={{ color: s.order?.business_unit === "ELETROS" ? "#C9960A" : "#5B2CA0" }}>{bu(s.order?.business_unit)}</span>
                        {fmtEta(s.estimated_arrival) && <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold text-blue-700" data-testid={`rota-stop-eta-${s.id}`}>ETA {fmtEta(s.estimated_arrival)}</span>}
                      </div>
                    </div>
                    <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: scolor }}>{slabel}</span>
                    {mutable && (
                      <div className="flex shrink-0 flex-col gap-1">
                        <button onClick={() => move(idx, -1)} disabled={idx === 0} className="grid h-6 w-6 place-items-center rounded border border-border disabled:opacity-30" data-testid={`rota-stop-up-${s.id}`}><ArrowUp size={13} /></button>
                        <button onClick={() => move(idx, 1)} disabled={idx === sel.stops.length - 1} className="grid h-6 w-6 place-items-center rounded border border-border disabled:opacity-30" data-testid={`rota-stop-down-${s.id}`}><ArrowDown size={13} /></button>
                      </div>
                    )}
                    {mutable && s.status === "PENDING" && <button onClick={() => removeStop(s.id)} className="grid h-7 w-7 shrink-0 place-items-center rounded-lg border border-red-200 text-red-500" data-testid={`rota-stop-remove-${s.id}`}><Trash2 size={14} /></button>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Entregas disponíveis */}
        {mutable && (
          <div>
            <h2 className="mb-2 text-sm font-bold text-brand-dark">Entregas liberadas (adicionar)</h2>
            {avail.length === 0 ? <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-[#6B5A69]">Nenhuma entrega liberada disponível.</div> : (
              <div className="grid gap-2 md:grid-cols-2" data-testid="rota-available-list">
                {avail.map((o) => (
                  <div key={o.id} className="flex items-center gap-2 rounded-2xl border border-border bg-white p-3" data-testid={`rota-avail-${o.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-bold text-brand-dark">{o.customer_name || "Cliente"}</div>
                      <div className="truncate text-[12px] text-[#8A7788]">{addr(o.delivery_address_snapshot)}</div>
                      <div className="text-[10px] uppercase tracking-wide" style={{ color: o.business_unit === "ELETROS" ? "#C9960A" : "#5B2CA0" }}>{bu(o.business_unit)}</div>
                    </div>
                    <button onClick={() => addStop(o.id)} className="flex shrink-0 items-center gap-1 rounded-xl px-3 py-1.5 text-[12px] font-bold text-[#2B1E4A]" style={{ background: GOLD }} data-testid={`rota-add-${o.id}`}><Plus size={13} /> Add</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Modal de preview da otimização */}
        {optPreview && (
          <div className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" data-testid="rota-optimize-modal">
            <div className="max-h-[85vh] w-full max-w-lg overflow-auto rounded-t-2xl bg-white p-5 sm:rounded-2xl">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-display text-lg font-bold" style={{ color: PURPLE }}>Nova sequência sugerida</h3>
                <button onClick={() => setOptPreview(null)} className="grid h-8 w-8 place-items-center rounded-lg border border-border" data-testid="rota-optimize-close"><X size={16} /></button>
              </div>
              <div className="mb-3 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-[#FBFAFD] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#9A8A98]">Depois · distância</div>
                  <div className="font-display text-xl font-bold" style={{ color: "#059669" }}>{fmtKm(optPreview.suggestion.total_distance_meters)}</div>
                </div>
                <div className="rounded-xl bg-[#FBFAFD] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#9A8A98]">Depois · duração</div>
                  <div className="font-display text-xl font-bold" style={{ color: "#059669" }}>{fmtDur(optPreview.suggestion.total_duration_seconds)}</div>
                </div>
              </div>
              <ol className="mb-4 space-y-1" data-testid="rota-optimize-sequence">
                {optPreview.suggestion.stops.map((st) => {
                  const stop = sel.stops.find((x) => x.id === st.stop_id);
                  return (
                    <li key={st.stop_id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                      <span className="grid h-6 w-6 place-items-center rounded-full text-[11px] font-bold text-white" style={{ background: PURPLE }}>{st.position}</span>
                      <span className="flex-1 truncate">{stop?.order?.customer_name || "Cliente"}</span>
                      {fmtEta(st.estimated_arrival) && <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold text-blue-700">ETA {fmtEta(st.estimated_arrival)}</span>}
                    </li>
                  );
                })}
              </ol>
              <div className="flex gap-2">
                <button onClick={applyOptimize} className="flex-1 rounded-xl py-3 text-sm font-bold text-white" style={{ background: "#059669" }} data-testid="rota-optimize-apply">Aplicar rota</button>
                <button onClick={() => setOptPreview(null)} className="flex-1 rounded-xl border border-border py-3 text-sm font-bold text-[#6B5A69]" data-testid="rota-optimize-keep">Manter ordem atual</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---- List view ----
  return (
    <div className="space-y-4 p-4 lg:p-8" data-testid="rota-routes-page">
      <div className="flex flex-wrap items-center gap-3 rounded-2xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${PURPLE}, ${MAGENTA})` }}>
        <span className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: GOLD }}><RouteIcon size={22} className="text-[#2B1E4A]" /></span>
        <div className="flex-1"><div className="text-[11px] font-bold uppercase tracking-[0.25em]" style={{ color: GOLD }}>CRIS ROTA</div><h1 className="font-display text-xl font-bold">Rotas</h1></div>
        <button onClick={createRoute} disabled={creating} className="flex items-center gap-1 rounded-xl px-3 py-2 text-sm font-bold text-[#2B1E4A]" style={{ background: GOLD }} data-testid="rota-new-route-btn"><Plus size={15} /> Nova rota</button>
      </div>

      {routes === null ? <div className="p-8 text-center text-sm text-[#9A8A98]">Carregando...</div> :
        routes.length === 0 ? <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-[#6B5A69]">Nenhuma rota. Crie uma nova para começar.</div> : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="rota-routes-list">
          {routes.map((r) => {
            const [label, cls] = RST[r.status] || ["—", "bg-gray-100"];
            return (
              <button key={r.id} onClick={() => openRoute(r.id)} className="rounded-2xl border border-border bg-white p-4 text-left transition hover:border-[#B5179E]" data-testid={`rota-route-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0"><div className="truncate font-display font-bold text-brand-dark">{r.name || "Rota"}</div><div className="text-[12px] text-[#8A7788]">{r.driver_name || "Sem entregador"}</div></div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${cls}`}>{label}</span>
                </div>
                <div className="mt-2 text-[12px] font-bold" style={{ color: PURPLE }}>{r.stop_count || 0} parada(s)</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
